<template>
  <div class="absolute w-full h-full" style="bottom:0;" v-if="councillor">
    <layout-person :color="color">
      <template v-slot:header>
        <!-- Portrait -->
        <candidate-profile-top class="relative pb-6 lg:pb-0">
          <div class="profile-picture pointer-events-none">
            <img
              :src="councillor.person.urlImage || fallbackImage"
              draggable="false"
              alt=""
              v-fallback="fallbackImage"
            />
          </div>
        </candidate-profile-top>
      </template>
      <!-- Content -->
      <template v-slot:information>
        <h1
          class="text-chplus-title-l font-bold mx-auto mt-2 mb-2"
          style="text-overflow: ellipsis;max-width: 95%;"
          :style="{ color }"
        >
          {{ `${councillor.person.firstName} ${councillor.person.lastName}` }}
        </h1>
        <div class="match-progress"></div>
        <p class="text-chplus-l text-interaction-dark mt-4">
          {{ councillor.title }}
        </p>

        <candidate-info-box :candidate="candidate" />

        <div
          class="flex justify-center mt-10 mb-3"
          v-if="!councillor.bio && councillor.profile"
        >
          <a
            :href="councillor.profile"
            target="_blank"
            class="w-fit rounded-lg p-3 bg-interaction-dark text-white"
          >
            {{ profileTitle }}
            <icon-link-profile />
          </a>
        </div>
      </template>
      <template v-slot:tabs>
        <content-tabs class="pointer-events-none">
          <content-tab
            :currentTab="'profile'"
            :color="color"
            tabName="profile"
            v-if="councillor.bio"
          >
            <icon-smiley />
          </content-tab>
        </content-tabs>
      </template>
      <!-- Tab Bodys -->
      <template v-if="councillor.bio" v-slot:content>
        <tab
          activeTab="profile"
          :color="color"
          tabName="profile"
          v-if="councillor.bio"
          :title="$t('views.single.cantonal_councillor.biography_title')"
        >
          <div
            class="councillor-bio text-chplus-candidate-info pb-6"
            :style="{ color: color }"
            v-html="councillor.bio"
          ></div>
        </tab>

        <div class="flex justify-center mt-10 mb-3" v-if="councillor.profile">
          <a
            :href="councillor.profile"
            target="_blank"
            class="w-fit rounded-lg p-3 bg-interaction-dark text-white"
          >
            {{ profileTitle }}
            <icon-link-profile />
          </a>
        </div>
      </template>
    </layout-person>
  </div>
</template>

<script>
import client from "api-client";
import { randomSample } from "@/utils/ArrayUtility";
import LayoutPerson from "@/components/layouts/LayoutPerson.vue";
import ContentTabs from "@/components/nav/ContentTabs";
import ContentTab from "@/components/nav/ContentTab";
import Tab from "@/components/people/Tab.vue";
import IconSmiley from "@/components/icons/IconSmiley.vue";
import IconLinkProfile from "@/components/icons/IconLinkProfile.vue";
import ShareLinkMixin from "@/utils/ShareLinkMixin";
import IsMobileMixin from "../../utils/IsMobileMixin";
import CandidateInfoBox from "@/components/people/CandidateInfoBox";
import CandidateProfileTop from "@/components/layouts/CandidateProfileTop";

export default {
  name: "CantonalCouncillor",
  props: {
    id: {
      type: [String, Number],
      required: true
    }
  },
  components: {
    LayoutPerson,
    ContentTabs,
    ContentTab,
    Tab,
    IconSmiley,
    IconLinkProfile,
    CandidateInfoBox,
    CandidateProfileTop
  },
  mixins: [ShareLinkMixin, IsMobileMixin],
  data() {
    return {
      councillor: null
    };
  },
  mounted() {
    this.fetchCouncillor();
  },
  methods: {
    async fetchCouncillor() {
      this.councillor = await client.fetchCantonalCouncillor(this.id);
    },
    share() {
      this.shareCurrentLink(
        this.$t("views.single.cantonal_councillor.share_subject"),
        this.$t("views.single.cantonal_councillor.share_message", {
          name: this.fullName
        })
      );
    }
  },
  computed: {
    fallbackImage() {
      if (this.councillor?.council.type === "judiciary") {
        if (this.councillor.person.gender === "m") {
          return require("@/assets/images/genicon-judge-male.svg");
        } else if (this.councillor.person.gender === "f") {
          return require("@/assets/images/genicon-judge-female.svg");
        } else {
          return randomSample([
            require("@/assets/images/genicon-judge-male.svg"),
            require("@/assets/images/genicon-judge-female.svg")
          ]);
        }
      } else {
        return require("@/assets/images/genicon-candidate-unknown.svg");
      }
    },
    candidate() {
      return {
        birthYear: this.councillor.person.birthYear,
        city: this.councillor.person.city,
        partyId: this.councillor.person.party?.id,
        partyLogo: this.councillor.person.party?.logo,
        partyName: this.councillor.person.party?.name,
        occupation: this.councillor.person.occupation
      };
    },
    color() {
      return this.councillor?.person?.party?.color || "#888EA8";
    },
    fullName() {
      if (!this.councillor?.person) return "";

      return `${
        this.councillor.person.firstName
      } ${this.councillor.person.lastName.toUpperCase()}`;
    },
    profileTitle() {
      if (!this.councillor.profile) return "";

      const url = new URL(this.councillor.profile);
      return url.hostname;
    }
  }
};
</script>

<style scoped>
.councillor-bio::v-deep ul,
.councillor-bio::v-deep ol {
  padding-left: 1.5rem;
  color: var(--color-candidate);
}

.councillor-bio::v-deep ul {
  list-style-type: disc;
}

.councillor-bio::v-deep ol {
  list-style-type: decimal;
}

.councillor-bio::v-deep p:not(:has(> strong)) {
  color: var(--color-candidate);
  text-align: justify;
}

.councillor-bio::v-deep li {
  text-align: left;
}

.councillor-bio::v-deep p > strong {
  text-align: center;
  margin-top: 1.5rem;
}

.profile-picture {
  width: 10.625rem;
  height: 10.625rem;
  flex: 0 0 10.625rem;
  position: relative;

  box-shadow: rgba(255, 238, 220, 0.2) 0 0 40px 10px;
  border-radius: 50%;

  img {
    position: absolute;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
    margin: 0 auto;
  }

  &::after {
    content: "";
    display: block;
    padding-bottom: 100%;
  }
}

.section {
  margin: 1.5rem 0;

  &:first-child {
    margin-top: -0.5rem;
  }
}

.content-tab-body {
  margin: 0 1em 0 1em;
}

.match-progress {
  width: 95%;
  height: 0.3125rem;
  margin-left: auto;
  margin-right: auto;
  background-color: var(--interaction-light);
  border-radius: 0.15625rem;
}

@media screen and (min-width: 768px) {
  .profile-picture {
    width: 12.0625rem;
    height: 12.0625rem;
    flex: 0 0 12.0625rem;
  }

  .match-progress {
    width: 70%;
  }
}
</style>
